/**
 * (Группа 1 · экраны 3–5) Восстановление пароля — поток из 3 шагов:
 *   A) email → отправка кода
 *   B) проверка 6-значного кода из письма (TTL 30 мин)
 *   C) новый пароль (≥12 символов, заглавные + цифры)
 *
 * Реализовано как один компонент со state-машиной шагов, экраны идут подряд
 * по UX-потоку (per feedback_design_grouped_by_feature_sequential).
 *
 * TODO(backend): endpoint'ы /v1/auth/recovery/{request,verify,reset} ещё не
 * реализованы — см. lib/api.ts. Поток и валидация UI готовы.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@hsc/ui';
import { requestRecovery, resetPassword, verifyRecovery } from '../../lib/api.js';

type Step = 'request' | 'verify' | 'reset' | 'done';

const STRONG_PASSWORD = /^(?=.*[A-ZА-Я])(?=.*\d).{12,}$/;

export function RecoveryPage(): JSX.Element {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitRequest(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await requestRecovery(email);
      setStep('verify');
    } catch {
      // Не раскрываем существование аккаунта — всё равно ведём на шаг verify.
      setStep('verify');
    } finally {
      setBusy(false);
    }
  }

  async function submitVerify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Введите 6-значный код из письма');
      return;
    }
    setBusy(true);
    try {
      const res = await verifyRecovery({ email, code });
      setResetToken(res.reset_token);
      setStep('reset');
    } catch {
      setError('Неверный или истёкший код');
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!STRONG_PASSWORD.test(password)) {
      setError('Минимум 12 символов, заглавные буквы и цифры');
      return;
    }
    setBusy(true);
    try {
      await resetPassword({ reset_token: resetToken, new_password: password });
      setStep('done');
    } catch {
      setError('Не удалось установить пароль. Запросите код заново.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="hsc-login">
      <div className="hsc-login__box" data-testid="recovery-card" data-step={step}>
        <div className="hsc-login__brand">
          <h1 className="hsc-login__title">Восстановление пароля</h1>
        </div>

        {step === 'request' && (
          <form onSubmit={submitRequest} noValidate>
            <div className="hsc-stack">
              <p className="hsc-login__subtitle">Шаг 1 из 3 — укажите email</p>
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" variant="primary" size="lg" loading={busy}>
                Отправить код
              </Button>
              <Link to="/login" className="admin-link">Назад ко входу</Link>
            </div>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={submitVerify} noValidate>
            <div className="hsc-stack">
              <p className="hsc-login__subtitle">Шаг 2 из 3 — код из письма (действует 30 минут)</p>
              <Input
                label="Код"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                {...(error ? { error } : {})}
              />
              <Button type="submit" variant="primary" size="lg" loading={busy}>
                Проверить код
              </Button>
              <button type="button" className="admin-link" onClick={() => setStep('request')}>
                Отправить код заново
              </button>
            </div>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={submitReset} noValidate>
            <div className="hsc-stack">
              <p className="hsc-login__subtitle">Шаг 3 из 3 — новый пароль</p>
              <Input
                label="Новый пароль"
                type="password"
                autoComplete="new-password"
                placeholder="≥12 символов, заглавные и цифры"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                {...(error ? { error } : {})}
              />
              <Button type="submit" variant="primary" size="lg" loading={busy}>
                Сохранить пароль
              </Button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div className="hsc-stack" data-testid="recovery-done">
            <p>Пароль обновлён. Теперь можно войти.</p>
            <Link to="/login" className="admin-link">Перейти ко входу</Link>
          </div>
        )}
      </div>
    </main>
  );
}
